import { usePriceVisibility, useUser, useUserSettings } from "@/composables/user";
import { LoginMarkerService } from "@/services/user/auth/login/loginMarkerService";
import { TokenService } from "@/services/user/auth/login/tokenService";
import { fetchUserSettings, UserSettings } from "@/services/user/settings";
import { AccountType, Right, Role, type User } from "@/services/user/types";
import { disallowBrowserTracking } from "@/services/analytics/helpers";
import * as PostHog from "@/services/analytics/postHog";
import * as GoogleAnalytics from "@/services/analytics/googleAnalytics";
import { useAppState } from "@/store/appState";
import { store as pinia } from "@/store";
import { Authentication, type AuthenticationPromise } from "@/services/user/auth/login/authenticationService";
import { addTokenToUrl, ApiClient, catch401 } from "@/services/apiClient";
import { createHomepageLink, router } from "@/router";
import { Facebook } from "@/services/user/auth/login/sso/FacebookLoginService";
import { logout as googleLogout } from "@/services/user/auth/login/sso/GoogleLoginService";
import { fetchUserBankInfo, UserBankInfo } from "@/services/user/bankInfo";
import { FallbackCurrencyId } from "@/services/currencies";
import { fetchUserAddress } from "@/services/user/profile";
import { type Address } from "@/services/addresses";

export async function checkUserOnInitialPageLoad(): Promise<AuthenticationPromise> {
  if (Authentication.isInProgressOrDone()) {
    return Authentication.promise;
  } else {
    const token = await TokenService.get();
    if (token) {
      return Authentication.run({ token });
    } else {
      return null;
    }
  }
}

export async function login(email: string, password: string): Promise<AuthenticationPromise> {
  Authentication.reset();
  return Authentication.run({ email, password });
}

export async function logout(invalidateInApi = true): Promise<void> {
  const { user } = useUser();
  const { userSettings, userBankInfo } = useUserSettings();

  if (user.value) {
    if (user.value.type === AccountType.Facebook) {
      Facebook.logout();
    } else if (user.value.type === AccountType.Google) {
      googleLogout();
    }

    if (invalidateInApi) {
      const url = addTokenToUrl("profile/logout");
      await ApiClient.put(url).catch(catch401);
    }

    TokenService.delete();
    user.value = undefined;
    userSettings.value = undefined;
    userBankInfo.value = undefined;
    router.push(createHomepageLink());
  }

  Authentication.reset();
}

export async function setupUserInApp(user: User): Promise<void> {
  TokenService.store(user.token);
  LoginMarkerService.markLogin();

  GoogleAnalytics.trackEvent(new GoogleAnalytics.LoginEvent(user.id));
  PostHog.identifyUser(user);

  if (user.role === Role.Admin) {
    disallowBrowserTracking();
  }

  const userStore = useUser();
  userStore.user.value = user;

  usePriceVisibility().arePricesVisible.value = user.showPrices;

  const userSettingsStore = useUserSettings();

  const promises: Promise<UserSettings | UserBankInfo | Address | undefined>[] = [
    fetchUserSettings().then((settings) => (userSettingsStore.userSettings.value = settings)), //
  ];

  if (userStore.hasRight(Right.Marketplace2_0)) {
    promises.push(fetchUserBankInfo().then((bankInfo) => (userSettingsStore.userBankInfo.value = bankInfo)));
    promises.push(fetchUserAddress().then((address) => (userSettingsStore.userAddress.value = address)));
  }

  await Promise.all(promises);

  const appStateStore = useAppState(pinia);
  const userPreferredCurrencyId = userSettingsStore.userSettings.value?.displayCurrencyId ?? FallbackCurrencyId;
  appStateStore.setCurrencyId(userPreferredCurrencyId);
}
